import { Button, Tooltip } from "@arco-design/web-react";
import { LoaderCircle, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { itineraryRequestFailureMessage } from "../service/itineraryErrorService";

import type {
  EnrichedContent,
  ItineraryDay,
  ItineraryEditor,
  PointDescriptionCompletionErrorCode,
  PointDescriptionCompletionRequest,
  PointDescriptionCompletionResult,
  SummarySlot,
} from "../types";
import { activeSummarySlots, composeDaySummary, editableSummary, effectiveSummarySource, effectiveSummaryText, hasSummaryArrangement, hasTextContent, isManualSummarySource, manualSummarySource, pointDescriptionFingerprint, summaryMaterialFingerprint, storedSummary } from "../service/itinerarySummaryService";
import { inferDayTitle } from "../service/itineraryEditorService";

type Props = {
  editor: ItineraryEditor;
  day: ItineraryDay;
  onChange: (editor: ItineraryEditor) => void;
  onCompletePointDescriptions?: (
    request: PointDescriptionCompletionRequest,
    signal?: AbortSignal,
  ) => Promise<PointDescriptionCompletionResult>;
  onRewriteSummary?: (slot: SummarySlot, signal?: AbortSignal, snapshot?: ItineraryEditor) => Promise<EnrichedContent>;
  summaryRewriting?: boolean;
  autoGenerateEmptyContent?: boolean;
  autoGenerateEmptySummaries?: boolean;
};

type PointCompletionFeedback = {
  kind: "success" | "error";
  message: string;
};

type PointCompletionState = {
  loading: boolean;
  automatic?: boolean;
  feedback?: PointCompletionFeedback;
};

type SummaryRewriteState = {
  loading: boolean;
  automatic?: boolean;
  feedback?: PointCompletionFeedback;
};

const pointDescriptionErrorMessages: Record<PointDescriptionCompletionErrorCode, string> = {
  config_missing: "AI 点位介绍尚未配置，请联系管理员。",
  timeout: "AI 生成超时，请重试。",
  rate_limited: "AI 请求过于频繁，请稍后重试。",
  upstream: "AI 服务暂不可用，请稍后重试。",
  invalid_output: "AI 返回内容不符合要求，请重试。",
  existing_description: "当前介绍已存在，请使用 AI 重写。",
};

/** 将服务端逐点稳定错误码转换为可操作反馈。 */
function resultFailureMessage(result: PointDescriptionCompletionResult, nodeId: string) {
  const errorCode = result.items.find((item) => (
    item.nodeId === nodeId && item.status !== "generated"
  ))?.errorCode;
  return errorCode ? pointDescriptionErrorMessages[errorCode] : "AI 补全失败，请重试。";
}

const summaries = [
  ["fullDaySummary", "全天摘要", "full_day"],
  ["morningSummary", "上午摘要", "morning"],
  ["afternoonSummary", "下午摘要", "afternoon"],
  ["eveningSummary", "晚上摘要", "evening"],
  ["summary", "本日摘要", "daytime"],
] as const;

const summarySourceFieldByField = {
  fullDaySummary: "fullDaySummarySource",
  morningSummary: "morningSummarySource",
  afternoonSummary: "afternoonSummarySource",
  eveningSummary: "eveningSummarySource",
  summary: "summarySource",
} as const;

/** 历史富文本换行在编辑态显示为普通换行，保存时继续保留客户输出的硬换行语义。 */
const editableMarkdown = editableSummary;

/** 按后端 Unicode 字符口径截断，不切断代理对。 */
function limitCharacters(value: string, maxLength: number) {
  return Array.from(value).slice(0, maxLength).join("");
}

/** 将编辑框换行还原为历史硬换行格式。 */
const storedMarkdown = storedSummary;

/** 编辑当前日文案、行程基础信息，并按现有 AI 能力批量重写。 */
export function ItineraryContentPanel({
  editor,
  day,
  onChange,
  onCompletePointDescriptions,
  onRewriteSummary,
  summaryRewriting = false,
  autoGenerateEmptyContent = false,
  autoGenerateEmptySummaries = true,
}: Props) {
  const [pointCompletionStates, setPointCompletionStates] = useState<Record<string, PointCompletionState>>({});
  const [summaryRewriteStates, setSummaryRewriteStates] = useState<Record<string, SummaryRewriteState>>({});
  const [batchRewriteDayIds, setBatchRewriteDayIds] = useState<Record<string, boolean>>({});
  const batchRewriteInFlightRef = useRef(new Set<string>());
  const [titleRecognitionErrorDayId, setTitleRecognitionErrorDayId] = useState<string | null>(null);
  const summaryGridRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef(editor);
  const onChangeRef = useRef(onChange);
  const onCompletePointDescriptionsRef = useRef(onCompletePointDescriptions);
  const onRewriteSummaryPropRef = useRef(onRewriteSummary);
  const pointCompletionSessionGenerationRef = useRef(0);
  const pointCompletionAbortRefs = useRef(new Map<string, AbortController>());
  const pointCompletionInFlightRefs = useRef(new Set<string>());
  const autoRewriteAttemptedRefs = useRef(new Set<string>());
  const summaryRewriteSessionGenerationRef = useRef(0);
  const summaryRewriteAbortRefs = useRef(new Map<string, AbortController>());
  const summaryRewriteInFlightRefs = useRef(new Set<string>());
  const completePointDescriptionRef = useRef<((
    nodeId: string,
    onlyIfEmpty?: boolean,
  ) => Promise<void>) | undefined>(undefined);
  const rewriteSummaryRef = useRef<((
    slot: SummarySlot,
    automatic?: boolean,
  ) => Promise<void>) | undefined>(undefined);
  const autoGenerateEmptyContentRef = useRef(autoGenerateEmptyContent);
  const activeDayIdRef = useRef(day.id);
  activeDayIdRef.current = day.id;
  autoGenerateEmptyContentRef.current = autoGenerateEmptyContent;
  editorRef.current = editor;
  onChangeRef.current = onChange;
  onCompletePointDescriptionsRef.current = onCompletePointDescriptions;
  onRewriteSummaryPropRef.current = onRewriteSummary;

  useEffect(() => {
    const textareas = Array.from(summaryGridRef.current?.querySelectorAll<HTMLTextAreaElement>(
      ".itinerary-summary-field:not(.itinerary-summary-day) textarea",
    ) ?? []);
    if (!textareas.length || typeof ResizeObserver === "undefined") return;
    let sharedHeight = textareas[0].getBoundingClientRect().height;
    const observer = new ResizeObserver((entries) => {
      // 忽略同步写入产生的通知，让任一时段都能继续拉高或缩短整组。
      const resized = entries.find(({ target }) => {
        const height = target.getBoundingClientRect().height;
        return height > 0 && height !== sharedHeight;
      });
      if (!resized) return;
      sharedHeight = resized.target.getBoundingClientRect().height;
      textareas.forEach((textarea) => { textarea.style.height = `${sharedHeight}px`; });
    });
    textareas.forEach((textarea) => observer.observe(textarea));
    return () => observer.disconnect();
  }, [day.id, editor.id, day.summary, day.isFullDay]);

  useEffect(() => {
    setPointCompletionStates({});
    setSummaryRewriteStates({});
    setBatchRewriteDayIds({});
    return () => {
      // 已取消的请求不算完成尝试，效果重建后仍需补全空白内容。
      autoRewriteAttemptedRefs.current.clear();
      pointCompletionSessionGenerationRef.current += 1;
      pointCompletionAbortRefs.current.forEach((controller) => controller.abort());
      pointCompletionAbortRefs.current.clear();
      pointCompletionInFlightRefs.current.clear();
      summaryRewriteSessionGenerationRef.current += 1;
      summaryRewriteAbortRefs.current.forEach((controller) => controller.abort());
      summaryRewriteAbortRefs.current.clear();
      summaryRewriteInFlightRefs.current.clear();
      batchRewriteInFlightRef.current.clear();
    };
  }, [editor.id]);

  const nodesById = new Map(editor.days.flatMap((item) => item.nodes).map((node) => [node.id, node]));
  const daySegments = editor.segments.filter((segment) => segment.startDayId === day.id && segment.transportMode !== "walk");
  const inspectionNodes = day.nodes.filter((node) => node.nodeType === "point");
  // 自动补全只需要在点位结构变化时重新扫描；描述/摘要回填会重建对象引用，不能因此重跑并发请求。
  const autoGenerationStructure = JSON.stringify(activeSummarySlots(day).map((slot) => summaryMaterialFingerprint(editor, day.id, slot)));
  const visibleSummaries = summaries.filter(([, , slot]) => slot === "daytime"
    || (day.isFullDay ? slot === "full_day" : slot !== "full_day"));
  const summaryModes = JSON.stringify(editor.days.map((item) => [item.id, item.isFullDay]));
  const previousSummaryModes = useRef(new Map(editor.days.map((item) => [item.id, item.isFullDay])));
  useEffect(() => {
    const modes = new Map<string, boolean>(JSON.parse(summaryModes));
    const modeChanged = [...modes].some(([id, mode]) => previousSummaryModes.current.has(id)
      && previousSummaryModes.current.get(id) !== mode);
    previousSummaryModes.current = modes;
    if (!modeChanged) return;
    summaryRewriteSessionGenerationRef.current += 1;
    summaryRewriteAbortRefs.current.forEach((controller) => controller.abort());
    summaryRewriteAbortRefs.current.clear();
    summaryRewriteInFlightRefs.current.clear();
    batchRewriteInFlightRef.current.clear();
    autoRewriteAttemptedRefs.current.clear();
    setSummaryRewriteStates({});
    setBatchRewriteDayIds({});
  }, [summaryModes]);
  const updateDay = (patch: Partial<ItineraryDay>) => {
    const current = editorRef.current;
    const next = { ...current, days: current.days.map((item) => item.id === day.id ? { ...item, ...patch } : item) };
    editorRef.current = next;
    onChangeRef.current(next);
  };

  const completePointDescription = async (nodeId: string, onlyIfEmpty = false) => {
    const requestedNode = editorRef.current.days.find((item) => item.id === day.id)?.nodes.find((node) => node.id === nodeId);
    if (
      !onCompletePointDescriptions
      || requestedNode?.nodeType !== "point"
      || !requestedNode.pointSpuId?.trim()
      || pointCompletionInFlightRefs.current.has(nodeId)
    ) return;
    const requestedEditorId = editor.id;
    const requestedDayId = day.id;
    const requestedNodeType = requestedNode.nodeType;
    const requestedPointSpuId = requestedNode.pointSpuId.trim();
    const requestedDescription = requestedNode.description;
    const requestedPoint = pointDescriptionFingerprint(editorRef.current, requestedDayId, nodeId);
    // 以当前编辑器中的内容为准：补全按钮始终请求生成，避免服务端保存的旧介绍阻断本地草稿。
    const overwrite = true;
    const sessionGeneration = pointCompletionSessionGenerationRef.current;
    const controller = new AbortController();
    pointCompletionAbortRefs.current.set(nodeId, controller);
    pointCompletionInFlightRefs.current.add(nodeId);
    setPointCompletionStates((current) => ({
      ...current,
      [nodeId]: { loading: true, automatic: onlyIfEmpty },
    }));

    try {
      const result = await onCompletePointDescriptions(
        { dayId: requestedDayId, nodeIds: [nodeId], overwrite, editor: editorRef.current },
        controller.signal,
      );
      if (
        controller.signal.aborted
        || pointCompletionSessionGenerationRef.current !== sessionGeneration
        || (onlyIfEmpty && !autoGenerateEmptyContentRef.current)
      ) return;

      const currentEditor = editorRef.current;
      if (currentEditor.id !== requestedEditorId) return;
      const currentDay = currentEditor.days.find((item) => item.id === requestedDayId);
      if (!currentDay) return;
      const currentNode = currentDay.nodes.find((node) => node.id === nodeId);
      if (
        currentNode?.nodeType !== requestedNodeType
        || currentNode.pointSpuId?.trim() !== requestedPointSpuId
        || currentNode.description !== requestedDescription
        || pointDescriptionFingerprint(currentEditor, requestedDayId, nodeId) !== requestedPoint
        || (onlyIfEmpty && hasTextContent(currentNode.description))
      ) return;
      const generatedItem = result.items.find((item) => (
        item.nodeId === nodeId
        && item.status === "generated"
        && item.description?.trim()
      ));
      if (generatedItem?.description && currentNode) {
        {
          const nextEditor = {
            ...currentEditor,
            days: currentEditor.days.map((item) => item.id === requestedDayId ? {
              ...item,
              nodes: item.nodes.map((node) => node.id === nodeId ? {
                ...node,
                description: generatedItem.description,
              } : node),
            } : item),
          };
          editorRef.current = nextEditor;
          onChangeRef.current(nextEditor);
          setPointCompletionStates((current) => ({
            ...current,
            [nodeId]: {
              loading: true,
              automatic: onlyIfEmpty,
              feedback: {
                kind: "success",
                message: overwrite ? "已重写当前考察点介绍。" : "已补全当前考察点介绍。",
              },
            },
          }));
        }
      } else {
        setPointCompletionStates((current) => ({
          ...current,
          [nodeId]: {
            loading: true,
            automatic: onlyIfEmpty,
            feedback: { kind: "error", message: resultFailureMessage(result, nodeId) },
          },
        }));
      }
    } catch (error) {
      if (
        !controller.signal.aborted
        && pointCompletionSessionGenerationRef.current === sessionGeneration
      ) {
        setPointCompletionStates((current) => ({
          ...current,
          [nodeId]: {
            loading: true,
            automatic: onlyIfEmpty,
            feedback: { kind: "error", message: itineraryRequestFailureMessage(error) },
          },
        }));
      }
    } finally {
      if (
        pointCompletionSessionGenerationRef.current === sessionGeneration
        && pointCompletionAbortRefs.current.get(nodeId) === controller
      ) {
        pointCompletionAbortRefs.current.delete(nodeId);
        pointCompletionInFlightRefs.current.delete(nodeId);
        setPointCompletionStates((current) => ({
          ...current,
          [nodeId]: {
            ...current[nodeId],
            loading: false,
          },
        }));
      }
    }
  };

  const rewriteSummary = async (slot: SummarySlot, automatic = false) => {
    const currentDay = editorRef.current.days.find((item) => item.id === day.id);
    if (!currentDay || (slot !== "daytime" && !activeSummarySlots(currentDay).includes(slot))) return;
    const requestKey = `${day.id}:${slot}`;
    if (!onRewriteSummary || summaryRewriting || summaryRewriteInFlightRefs.current.has(requestKey)) return;
    const requestedDayId = day.id;
    const sessionGeneration = summaryRewriteSessionGenerationRef.current;
    const controller = new AbortController();
    summaryRewriteAbortRefs.current.set(requestKey, controller);
    summaryRewriteInFlightRefs.current.add(requestKey);
    setSummaryRewriteStates((current) => ({
      ...current,
      [requestKey]: { loading: true, automatic },
    }));

    try {
      const outcome = await composeDaySummary({
        dayId: requestedDayId, slot, automatic, signal: controller.signal,
        getEditor: () => editorRef.current,
        onChange: (next) => { editorRef.current = next; onChangeRef.current(next); },
        enrich: (request, signal) => onRewriteSummary(slot, signal, request.editor),
        completePointDescriptions: onCompletePointDescriptions,
        isCurrent: () => summaryRewriteSessionGenerationRef.current === sessionGeneration
          && (!automatic || autoGenerateEmptyContentRef.current),
      });
      if (controller.signal.aborted || summaryRewriteSessionGenerationRef.current !== sessionGeneration) return;
      if (outcome === "empty") return;
      if (outcome === "stale" || outcome === "manual") {
        setSummaryRewriteStates((current) => ({ ...current, [requestKey]: { loading: true, automatic,
          feedback: { kind: "success", message: "请求期间已有人工修改，未覆盖当前摘要。" },
        } }));
        return;
      }
      setSummaryRewriteStates((current) => ({
        ...current,
        [requestKey]: {
          loading: true,
          automatic,
          feedback: { kind: "success", message: `已重写${summaries.find((item) => item[2] === slot)?.[1] ?? "时段"}。` },
        },
      }));
    } catch (error) {
      const latestDay = editorRef.current.days.find((item) => item.id === requestedDayId);
      if (
        !controller.signal.aborted
        && summaryRewriteSessionGenerationRef.current === sessionGeneration
        && latestDay
        && effectiveSummaryText(latestDay, slot) === effectiveSummaryText(currentDay, slot)
        && effectiveSummarySource(latestDay, slot) === effectiveSummarySource(currentDay, slot)
      ) {
        setSummaryRewriteStates((current) => ({
          ...current,
          [requestKey]: {
            loading: true,
            automatic,
            feedback: { kind: "error", message: itineraryRequestFailureMessage(error) },
          },
        }));
      }
    } finally {
      if (
        summaryRewriteSessionGenerationRef.current === sessionGeneration
        && summaryRewriteAbortRefs.current.get(requestKey) === controller
      ) {
        summaryRewriteAbortRefs.current.delete(requestKey);
        summaryRewriteInFlightRefs.current.delete(requestKey);
        setSummaryRewriteStates((current) => ({
          ...current,
          [requestKey]: {
            ...current[requestKey],
            loading: false,
          },
        }));
      }
    }
  };

  const isBatchRewriting = Boolean(batchRewriteDayIds[day.id]);
  const hasActiveDayRequest = summaries.some(([, , slot]) => summaryRewriteStates[`${day.id}:${slot}`]?.loading)
    || inspectionNodes.some((node) => pointCompletionStates[node.id]?.loading);
  const canRewritePage = Boolean(onRewriteSummary
    || onCompletePointDescriptions && inspectionNodes.some((node) => node.pointSpuId?.trim()));

  const rewriteCurrentPage = async () => {
    if (!canRewritePage || summaryRewriting || hasActiveDayRequest || batchRewriteInFlightRef.current.has(day.id)) return;
    const requestedDayId = day.id;
    const generation = summaryRewriteSessionGenerationRef.current;
    const slots = visibleSummaries.filter(([, , slot]) => slot !== "daytime" || !day.isFullDay && hasTextContent(day.summary)).map(([, , slot]) => slot);
    batchRewriteInFlightRef.current.add(requestedDayId);
    setBatchRewriteDayIds((current) => ({ ...current, [requestedDayId]: true }));
    try {
      // 先处理介绍，再从最新草稿生成正文，避免正文消费上一次介绍。
      await Promise.allSettled(inspectionNodes.filter((item) => item.pointSpuId?.trim())
        .map((node) => completePointDescription(node.id)));
      if (summaryRewriteSessionGenerationRef.current !== generation) return;
      const readySlots = slots.filter((slot) => {
        if (slot === "daytime") return true;
        const latestDay = editorRef.current.days.find((item) => item.id === requestedDayId);
        if (!latestDay || !activeSummarySlots(latestDay).includes(slot)) return false;
        const missing = latestDay.nodes
          .some((node) => (slot === "full_day" || node.timeSlot === slot) && node.nodeType === "point" && !hasTextContent(node.description));
        if (missing) setSummaryRewriteStates((current) => ({ ...current, [`${requestedDayId}:${slot}`]: {
          loading: false, feedback: { kind: "error", message: "点位介绍尚未补齐，原正文已保留。请先补充点位介绍后重试。" },
        } }));
        return !missing;
      });
      await Promise.allSettled(readySlots.map((slot) => rewriteSummary(slot)));
    } finally {
      if (summaryRewriteSessionGenerationRef.current === generation) {
        batchRewriteInFlightRef.current.delete(requestedDayId);
        setBatchRewriteDayIds((current) => ({ ...current, [requestedDayId]: false }));
      }
    }
  };

  // 自动 effect 只读取稳定引用，避免 loading 状态更新导致函数身份变化并取消在途请求。
  completePointDescriptionRef.current = completePointDescription;
  rewriteSummaryRef.current = rewriteSummary;

  useEffect(() => {
    if (!autoGenerateEmptyContent || day.legacyCompatibility) {
      autoRewriteAttemptedRefs.current.clear();
      return;
    }
    if (summaryRewriting || summaryRewriteInFlightRefs.current.size || batchRewriteInFlightRef.current.has(day.id)) return;
    const generation = summaryRewriteSessionGenerationRef.current;
    const isCurrent = () => generation === summaryRewriteSessionGenerationRef.current
      && activeDayIdRef.current === day.id && autoGenerateEmptyContentRef.current;
    void (async () => {
      // 同日点位介绍并行补齐；共享 runtime 队列负责跨入口限流，正文仍等待介绍完成。
      const pointTasks: Promise<void>[] = [];
      for (const node of day.nodes) {
        if (!isCurrent()) return;
        if (node.nodeType !== "point" || !node.pointSpuId?.trim()) continue;
        const currentNode = editorRef.current.days.find((item) => item.id === day.id)?.nodes.find((item) => item.id === node.id);
        if (hasTextContent(currentNode?.description)) continue;
        const key = `${editor.id}:${day.id}:${node.id}:description:${node.pointSpuId}`;
        if (!onCompletePointDescriptionsRef.current || autoRewriteAttemptedRefs.current.has(key)) continue;
        autoRewriteAttemptedRefs.current.add(key);
        pointTasks.push(completePointDescriptionRef.current?.(node.id, true) ?? Promise.resolve());
      }
      await Promise.allSettled(pointTasks);
      if (!isCurrent()) return;
      if (!autoGenerateEmptySummaries) return;
      for (const slot of activeSummarySlots(day)) {
        if (!isCurrent()) return;
        const current = editorRef.current;
        const currentDay = current.days.find((item) => item.id === day.id);
        if (!currentDay || !activeSummarySlots(currentDay).includes(slot)
          || effectiveSummarySource(currentDay, slot) === "ai_composed" || isManualSummarySource(effectiveSummarySource(currentDay, slot) ?? undefined)
          || hasTextContent(effectiveSummaryText(currentDay, slot)) || !hasSummaryArrangement(current, day.id, slot)) continue;
        // 已经尝试但仍缺失的介绍交给用户重试，避免在同轮正文中再次调用点位接口。
        if (currentDay.nodes.some((node) => (slot === "full_day" || node.timeSlot === slot) && node.nodeType === "point" && !hasTextContent(node.description))) continue;
        const key = `${editor.id}:${day.id}:${slot}:${summaryMaterialFingerprint(current, day.id, slot)}`;
        if (!onRewriteSummaryPropRef.current || autoRewriteAttemptedRefs.current.has(key)) continue;
        autoRewriteAttemptedRefs.current.add(key);
        await rewriteSummaryRef.current?.(slot, true);
      }
    })();
  }, [editor.id, autoGenerateEmptyContent, autoGenerateEmptySummaries, summaryRewriting, day.id, day.legacyCompatibility,
    autoGenerationStructure, Boolean(onRewriteSummary), Boolean(onCompletePointDescriptions)]);

  return (
    <section className="itinerary-content-panel" aria-label={`第${day.dayNumber}天内容编辑`}>
      <header className="itinerary-content-heading">
        <div><span>STEP 2 · 行程内容编辑</span><h2>第{day.dayNumber}天内容</h2><p>直接修改会自动保存到本地草稿。</p></div>
        <div>
          <Button
            icon={<Sparkles size={15} />}
            loading={isBatchRewriting}
            disabled={!canRewritePage || summaryRewriting || isBatchRewriting || hasActiveDayRequest}
            onClick={() => void rewriteCurrentPage()}
          >
            批量重写
          </Button>
        </div>
      </header>

      <div className="itinerary-content-body">
        <section className="itinerary-content-section" aria-label="行程基础信息">
          <header className="itinerary-content-section-heading">
            <div><h3>行程基础信息</h3><span>标题会用于客户预览、分享与导出文件。</span></div>
            <b>全程统一</b>
          </header>
          <div className="itinerary-content-section-body">
            <label>行程标题<input value={editor.title ?? ""} maxLength={300} onChange={(event) => onChange({ ...editor, title: limitCharacters(event.target.value, 300) })} /></label>
          </div>
        </section>

        <section className="itinerary-content-section" aria-label="① 本日标题与时段摘要">
          <header className="itinerary-content-section-heading">
            <div><h3>① 本日标题与时段摘要</h3><span>客户表格和行程正文会直接使用这些内容。</span></div>
            <b>客户可见</b>
          </header>
          <div className="itinerary-content-section-body">
            <div className="itinerary-day-title-field">
              <label htmlFor={`itinerary-day-title-${day.id}`}>本日标题</label>
              <div className="itinerary-day-title-input">
                <input id={`itinerary-day-title-${day.id}`} placeholder="待定" value={day.dayTitle ?? ""} maxLength={300} onChange={(event) => {
                  setTitleRecognitionErrorDayId(null);
                  updateDay({ dayTitle: limitCharacters(event.target.value, 300) });
                }} />
                <Button
                  htmlType="button"
                  onClick={() => {
                    const dayTitle = inferDayTitle(day);
                    if (!dayTitle) {
                      setTitleRecognitionErrorDayId(day.id);
                      return;
                    }
                    setTitleRecognitionErrorDayId(null);
                    updateDay({ dayTitle: limitCharacters(dayTitle, 300) });
                  }}
                >自动识别</Button>
              </div>
              {titleRecognitionErrorDayId === day.id ? (
                <span className="itinerary-day-title-error" role="alert">未能识别标题，请确认骨架编辑中的本日起点和终点为住宿点或学校校区，且名称完整，原标题已保留。</span>
              ) : null}
            </div>
            <div className="itinerary-summary-grid" ref={summaryGridRef}>
              {visibleSummaries.map(([field, label, slot]) => {
                if (slot === "daytime" && !editableMarkdown(day.summary).replace(/<[^>]*>|&nbsp;|&#160;|&#xA0;/gi, "").trim()) return null;
                const state = summaryRewriteStates[`${day.id}:${slot}`];
                const sourceField = summarySourceFieldByField[field];
                const source = effectiveSummarySource(day, slot) ?? "ai_generated";
                const textareaId = `itinerary-${field}-${day.id}`;
                const loadingStatusId = `${textareaId}-loading`;
                return (
                  <div className={`itinerary-summary-field${slot === "daytime" ? " itinerary-summary-day" : ""}`} key={field}>
                    <div className="itinerary-summary-heading">
                      <label htmlFor={textareaId}>{label}</label>
                      <span className={`itinerary-summary-source-badge is-${source}`}>
                        {isManualSummarySource(source) ? "人工编辑" : "AI生成"}
                      </span>
                      {slot === "daytime" ? <span className="itinerary-summary-legacy-badge">历史兼容</span> : null}
                      {state?.loading && state.automatic ? (
                        <span
                          id={loadingStatusId}
                          className="itinerary-auto-ai-status"
                          role="status"
                          aria-live="polite"
                        >
                          <LoaderCircle size={14} aria-hidden="true" />
                          AI 正在自动生成摘要
                        </span>
                      ) : null}
                      <Tooltip content={slot === "daytime" ? "根据上午和下午的点位与交通事实重写本日摘要，不生成晚上安排" : slot === "full_day" ? "按当天全部点位与交通事实重写全天摘要，包含晚间安排" : "按当前时段的点位和可用交通事实重写摘要"} position="top">
                        <span className="itinerary-point-ai-trigger">
                          <Button
                            className="itinerary-point-ai-button"
                            size="mini"
                            icon={<Sparkles size={13} />}
                            loading={state?.loading}
                            disabled={!onRewriteSummary || summaryRewriting || state?.loading || isBatchRewriting || inspectionNodes.some((node) => pointCompletionStates[node.id]?.loading)}
                            aria-label={`AI 重写${label}`}
                            onClick={() => void rewriteSummary(slot)}
                          >
                            AI 重写
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                    {slot === "daytime" ? <p className="itinerary-summary-legacy-note">仅用于维护旧系统导入的正文，当前正文请使用{day.isFullDay ? "全天摘要" : "上午、下午、晚上摘要"}。</p> : null}
                    <div className="itinerary-point-description-editor">
                      <textarea
                        id={textareaId}
                        disabled={state?.loading && !state.automatic}
                        aria-describedby={state?.loading ? loadingStatusId : undefined}
                        aria-busy={state?.loading || undefined}
                        value={editableMarkdown(effectiveSummaryText(day, slot))}
                        onChange={(event) => {
                          updateDay({ [field]: storedMarkdown(event.target.value), [sourceField]: manualSummarySource(source) } as Partial<ItineraryDay>);
                          const requestKey = `${day.id}:${slot}`;
                          setSummaryRewriteStates((current) => current[requestKey]?.feedback?.kind === "error"
                            ? { ...current, [requestKey]: { ...current[requestKey], feedback: undefined } }
                            : current);
                        }}
                      />
                      {state?.loading && !state.automatic ? (
                        <div
                          id={loadingStatusId}
                          className="itinerary-point-ai-overlay"
                          role="status"
                          aria-live="polite"
                        >
                          <LoaderCircle size={18} aria-hidden="true" />
                          <span>AI 正在生成...</span>
                        </div>
                      ) : null}
                    </div>
                    {state?.feedback ? (
                      <p
                        className={`itinerary-point-ai-feedback is-${state.feedback.kind}`}
                        role={state.feedback.kind === "error" ? "alert" : "status"}
                        aria-live="polite"
                      >
                        {state.feedback.message}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="itinerary-content-section" aria-label="② 点位介绍">
          <header className="itinerary-content-section-heading">
            <div><h3>② 点位介绍</h3><span>点位介绍作为时段正文素材；修改后点击对应时段 AI 重写。</span></div>
            <b>{inspectionNodes.length} 个考察点</b>
          </header>
          <div className="itinerary-content-section-body itinerary-point-description-list">
            {inspectionNodes.length ? inspectionNodes.map((node) => {
              const completionState = pointCompletionStates[node.id];
              const hasDescription = Boolean(node.description?.trim());
              const actionLabel = hasDescription ? "AI 重写" : "AI 补全";
              const canComplete = Boolean(onCompletePointDescriptions && node.pointSpuId?.trim());
              const tooltip = !onCompletePointDescriptions
                ? "点位介绍 AI 补全暂不可用"
                : !node.pointSpuId?.trim()
                  ? "该考察点未关联主数据，暂无法 AI 补全"
                  : hasDescription
                    ? "重新生成并替换当前考察点介绍"
                    : "仅补全当前考察点介绍";
              const textareaId = `itinerary-point-description-${node.id}`;
              const loadingStatusId = `${textareaId}-loading`;
              return (
                <div
                  className="itinerary-point-description-item"
                  key={node.id}
                  role="group"
                  aria-label={`${node.title}介绍编辑`}
                >
                  <div className="itinerary-point-description-heading">
                    <label htmlFor={textareaId}>{node.title}介绍</label>
                    {completionState?.loading && completionState.automatic ? (
                      <span
                        id={loadingStatusId}
                        className="itinerary-auto-ai-status"
                        role="status"
                        aria-live="polite"
                      >
                        <LoaderCircle size={14} aria-hidden="true" />
                        AI 正在自动补全介绍
                      </span>
                    ) : null}
                    <Tooltip content={tooltip} position="top">
                      <span className="itinerary-point-ai-trigger">
                        <Button
                          className="itinerary-point-ai-button"
                          size="mini"
                          icon={<Sparkles size={13} />}
                          loading={completionState?.loading}
                          disabled={!canComplete || completionState?.loading || isBatchRewriting || summaryRewriting || summaryRewriteInFlightRefs.current.size > 0}
                          aria-label={`${actionLabel}${node.title}介绍`}
                          onClick={() => void completePointDescription(node.id)}
                        >
                          {actionLabel}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                  <div className="itinerary-point-description-editor">
                    <textarea
                      id={textareaId}
                      disabled={completionState?.loading && !completionState.automatic}
                      aria-describedby={completionState?.loading ? loadingStatusId : undefined}
                      aria-busy={completionState?.loading || undefined}
                      value={editableMarkdown(node.description)}
                      onChange={(event) => {
                        updateDay({
                          nodes: day.nodes.map((item) => item.id === node.id ? { ...item, description: storedMarkdown(event.target.value) } : item),
                        });
                        setPointCompletionStates((current) => current[node.id]?.feedback?.kind === "error"
                          ? { ...current, [node.id]: { ...current[node.id], feedback: undefined } }
                          : current);
                      }}
                    />
                    {completionState?.loading && !completionState.automatic ? (
                      <div
                        id={loadingStatusId}
                        className="itinerary-point-ai-overlay"
                        role="status"
                        aria-live="polite"
                      >
                        <LoaderCircle size={18} aria-hidden="true" />
                        <span>AI 正在生成...</span>
                      </div>
                    ) : null}
                  </div>
                  {completionState?.feedback ? (
                    <p
                      className={`itinerary-point-ai-feedback is-${completionState.feedback.kind}`}
                      role={completionState.feedback.kind === "error" ? "alert" : "status"}
                      aria-live="polite"
                    >
                      {completionState.feedback.message}
                    </p>
                  ) : null}
                </div>
              );
            }) : <p className="itinerary-content-empty">本日暂无考察点，请先在骨架编辑中添加。</p>}
          </div>
        </section>

        <section className="itinerary-content-section" aria-label="③ 交通路线说明">
          <header className="itinerary-content-section-heading">
            <div><h3>③ 交通路线说明</h3><span>结构数据保留里程、时长和班次，人工只补充组织说明。</span></div>
            <b>{daySegments.length} 条线</b>
          </header>
          <div className="itinerary-content-section-body">
            {daySegments.length ? daySegments.map((segment) => {
              const from = nodesById.get(segment.fromNodeId)?.title ?? "起点";
              const to = nodesById.get(segment.toNodeId)?.title ?? "终点";
              return (
                <label key={segment.id}>{from}到{to}的交通说明
                  <textarea
                    value={editableMarkdown(segment.segmentDescription)}
                    onChange={(event) => onChange({
                      ...editor,
                      segments: editor.segments.map((item) => item.id === segment.id ? { ...item, segmentDescription: storedMarkdown(event.target.value) } : item),
                    })}
                  />
                </label>
              );
            }) : <p className="itinerary-content-empty">本日暂无交通路线。</p>}
          </div>
        </section>

      </div>
    </section>
  );
}
